import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from './context/AuthContext';
import { useProperty } from './context/PropertyContext';
import { useSubscription } from './context/SubscriptionContext';
import Notification from './components/Notification';
import Spinner from './components/Spinner';
import LimitReachedModal from './components/LimitReachedModal';
import AdvancedROIMetrics from './components/AdvancedROIMetrics';
import PropertyComparison from './components/PropertyComparison';
import { fetchPropertyData, recalculateROI } from './services/propertyService';
import './styles/FormPage.css';

const FormPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { updatePropertyData, updateRoiResults } = useProperty();
  const { canPerformAction, trackPropertyAnalysis, upgradeSubscription } = useSubscription();
  const { address, isGuest } = location.state || {};

  const [formData, setFormData] = useState({
    capRate: '',
    propertyPrice: '',
    monthlyHOA: '',
    propertyTax: '',
    yearlyInsurance: '',
    managementFeePercentage: '',
    yearlyMaintenance: '',
    monthlyRent: '',
    vacancyRate: '',
    additionalExpenses: '0',
    downpaymentPercentage: '100',
    propertyAddress: address
  });

  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitType, setLimitType] = useState('property_analysis');
  const [recalculateCount, setRecalculateCount] = useState(parseInt(localStorage.getItem('guestRecalculateCount') || '0'));

  useEffect(() => {
    if (address) {
      loadPropertyData(address);
    }
  }, [address]);

  const loadPropertyData = async (propertyAddress) => {
    // Check subscription limits before making API call
    if (user && !canPerformAction('property_analysis')) {
      setLimitType('property_analysis');
      setShowLimitModal(true);
      return;
    }

    setLoading(true);
    try {
      const data = await fetchPropertyData(propertyAddress, user?.userId);
      updateFormFields(data);
      
      // Track usage after successful API call
      if (user) {
        trackPropertyAnalysis();
      }
      
      setNotification({ 
        show: true, 
        message: 'Property data loaded successfully', 
        type: 'success' 
      });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
    } catch (error) {
      console.error('Error loading property data:', error);
      
      // Handle monthly limit gracefully
      if (error.message.includes('Monthly limit reached')) {
        setLimitType('property_analysis');
        setShowLimitModal(true);
      } else {
        setNotification({
          show: true,
          message: `Failed to fetch data: ${error.message}`,
          type: 'error'
        });
        setTimeout(() => setNotification({ show: false, message: '', type: '' }), 5000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSliderChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value.toString()
    });
  };

  const getSliderConfig = (fieldName) => {
    const configs = {
      propertyPrice: { min: 0, max: 2000000, step: 5000 },
      monthlyRent: { min: 0, max: 10000, step: 50 },
      propertyTax: { min: 0, max: 50000, step: 100 },
      yearlyInsurance: { min: 0, max: 10000, step: 50 },
      monthlyHOA: { min: 0, max: 1000, step: 10 },
      managementFeePercentage: { min: 0, max: 20, step: 0.5 },
      vacancyRate: { min: 0, max: 20, step: 0.5 },
      yearlyMaintenance: { min: 0, max: 20000, step: 100 },
      additionalExpenses: { min: 0, max: 10000, step: 25 },
      downpaymentPercentage: { min: 0, max: 100, step: 1 }
    };
    return configs[fieldName] || { min: 0, max: 100, step: 1 };
  };

  const handleRecalculate = async () => {
    // Check subscription limits before making API call
    if (user && !canPerformAction('property_analysis')) {
      setLimitType('property_analysis');
      setShowLimitModal(true);
      return;
    }

    // For guest users, allow one recalculate, then show auth prompt
    if (isGuest && !user && recalculateCount >= 1) {
      setShowAuthPrompt(true);
      return;
    }

    setLoading(true);
    try {
      const data = await recalculateROI(formData, user?.userId);
      updateFormFields(data);
      
      // Track usage after successful API call
      if (user) {
        trackPropertyAnalysis();
      }
      
      // Track guest recalculates
      if (isGuest && !user) {
        const newCount = recalculateCount + 1;
        setRecalculateCount(newCount);
        localStorage.setItem('guestRecalculateCount', newCount.toString());
      }
      
      setNotification({
        show: true,
        message: 'Recalculation successful',
        type: 'success'
      });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
    } catch (error) {
      console.error('Error during recalculation:', error);
      
      // Handle monthly limit gracefully
      if (error.message.includes('Monthly limit reached')) {
        setLimitType('property_analysis');
        setShowLimitModal(true);
      } else {
        setNotification({
          show: true,
          message: `Recalculate failed: ${error.message}`,
          type: 'error'
        });
        setTimeout(() => setNotification({ show: false, message: '', type: '' }), 5000);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const updateFormFields = (data) => {
    const updatedData = { ...formData };
    
    // Update form fields with responseData
    Object.keys(data).forEach(key => {
      if (formData.hasOwnProperty(key)) {
        updatedData[key] = data[key];
      }
    });
  
    setFormData(updatedData);
    
    // Update property context for chatbot
    updatePropertyData(updatedData);
    
    // If ROI results are in the data, update those too
    if (data.annualReturnRate || data.capRate || data.monthlyCashFlow) {
      updateRoiResults(data);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    
    // If it's already a string with a dollar sign, return as is
    if (typeof value === 'string' && value.startsWith('$')) return value;
    
    // Otherwise format it
    return `$${parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };
  
  // Calculate simplified ROI metrics
  const calculateROI = () => {
    const price = parseFloat(formData.propertyPrice) || 0;
    if (price === 0) return { annualReturn: 0, cashOnCash: 0 };
    
    const monthlyRent = parseFloat(formData.monthlyRent) || 0;
    const annualRent = monthlyRent * 12;
    const vacancyLoss = annualRent * (parseFloat(formData.vacancyRate) / 100 || 0);
    const managementFee = annualRent * (parseFloat(formData.managementFeePercentage) / 100 || 0);
    const annualHOA = (parseFloat(formData.monthlyHOA) || 0) * 12;
    const yearlyTax = parseFloat(formData.propertyTax) || 0;
    const yearlyInsurance = parseFloat(formData.yearlyInsurance) || 0;
    const yearlyMaintenance = parseFloat(formData.yearlyMaintenance) || 0;
    const annualAdditionalExpenses = (parseFloat(formData.additionalExpenses) || 0) * 12;
    
    const totalExpenses = vacancyLoss + managementFee + annualHOA + yearlyTax + yearlyInsurance + yearlyMaintenance + annualAdditionalExpenses;
    const netOperatingIncome = annualRent - totalExpenses;
    
    // Cap Rate
    // monthly mortage is not included in capRate
    const capRate = ((netOperatingIncome +annualAdditionalExpenses)/ price) * 100;
    
    // Cash on Cash Return
    const downPaymentPercent = parseFloat(formData.downpaymentPercentage) / 100 || 1;
    const downPayment = price * downPaymentPercent;
    const cashOnCash = downPayment > 0 ? (netOperatingIncome / downPayment) * 100 : 0;
    
    return {
      capRate: capRate.toFixed(2),
      cashOnCash: cashOnCash.toFixed(2),
      netOperatingIncome: netOperatingIncome.toFixed(0)
    };
  };
  
  const roiMetrics = calculateROI();
  
  return (
    <div className="property-form-page">
      {notification.show && (
        <Notification 
          message={notification.message} 
          type={notification.type} 
        />
      )}
      
      {/* Header Section */}
      <div className="page-header">
        <div className="header-content">
          <h1>Property Analysis</h1>
          <h2 className="property-address">{address || "Property Details"}</h2>
          
          {isGuest && !user && (
            <div className="guest-banner">
              <p>🎯 You're viewing as a guest. <a href="/signup">Sign up</a> or <a href="/login">log in</a> to save your analysis and unlock full features!</p>
            </div>
          )}
        </div>
      </div>

      {/* Main Content Container */}
      <div className="main-content">
        
        {/* ROI Metrics Card */}
        <div className="content-card">
          <h3 className="card-title">Key Metrics</h3>
          <div className={`roi-metrics ${loading ? 'loading' : ''}`}>
            {loading && (
              <div className="loading-overlay">
                <Spinner size="medium" message="Calculating ROI..." />
              </div>
            )}
            <div className="metric">
              <span className="metric-value">{roiMetrics.capRate}%</span>
              <span className="metric-label">Cap Rate</span>
            </div>
            <div className="metric">
              <span className="metric-value">{roiMetrics.cashOnCash}%</span>
              <span className="metric-label">Cash on Cash</span>
            </div>
            <div className="metric">
              <span className="metric-value">{formatCurrency(roiMetrics.netOperatingIncome)}</span>
              <span className="metric-label">Annual NOI</span>
            </div>
          </div>
        </div>

        {/* Advanced ROI Metrics */}
        <AdvancedROIMetrics formData={formData} />

        {/* Property Details Form */}
        <div className="content-card">
          <h3 className="card-title">Property Details</h3>
          <form className="property-form">
            <div className="form-columns">
              <div className="form-column">
                <div className="form-group">
                  <label htmlFor="propertyPrice">Purchase Price</label>
                  <input 
                    type="text" 
                    id="propertyPrice"
                    name="propertyPrice" 
                    value={formData.propertyPrice} 
                    onChange={handleChange} 
                    className="form-control"
                  />
                  <input
                    type="range"
                    min={getSliderConfig('propertyPrice').min}
                    max={getSliderConfig('propertyPrice').max}
                    step={getSliderConfig('propertyPrice').step}
                    value={parseFloat(formData.propertyPrice) || 0}
                    onChange={(e) => handleSliderChange('propertyPrice', e.target.value)}
                    className="form-slider"
                  />
                  <div className="slider-labels">
                    <span>$0</span>
                    <span>$2M</span>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="monthlyRent">Monthly Rent</label>
                  <input 
                    type="text" 
                    id="monthlyRent"
                    name="monthlyRent" 
                    value={formData.monthlyRent} 
                    onChange={handleChange}
                    className="form-control" 
                  />
                  <input
                    type="range"
                    min={getSliderConfig('monthlyRent').min}
                    max={getSliderConfig('monthlyRent').max}
                    step={getSliderConfig('monthlyRent').step}
                    value={parseFloat(formData.monthlyRent) || 0}
                    onChange={(e) => handleSliderChange('monthlyRent', e.target.value)}
                    className="form-slider"
                  />
                  <div className="slider-labels">
                    <span>$0</span>
                    <span>$10K</span>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="propertyTax">Annual Property Tax</label>
                  <input 
                    type="text" 
                    id="propertyTax"
                    name="propertyTax" 
                    value={formData.propertyTax} 
                    onChange={handleChange}
                    className="form-control" 
                  />
                  <input
                    type="range"
                    min={getSliderConfig('propertyTax').min}
                    max={getSliderConfig('propertyTax').max}
                    step={getSliderConfig('propertyTax').step}
                    value={parseFloat(formData.propertyTax) || 0}
                    onChange={(e) => handleSliderChange('propertyTax', e.target.value)}
                    className="form-slider"
                  />
                  <div className="slider-labels">
                    <span>$0</span>
                    <span>$50K</span>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="yearlyInsurance">Annual Insurance</label>
                  <input 
                    type="text" 
                    id="yearlyInsurance"
                    name="yearlyInsurance" 
                    value={formData.yearlyInsurance} 
                    onChange={handleChange}
                    className="form-control" 
                  />
                  <input
                    type="range"
                    min={getSliderConfig('yearlyInsurance').min}
                    max={getSliderConfig('yearlyInsurance').max}
                    step={getSliderConfig('yearlyInsurance').step}
                    value={parseFloat(formData.yearlyInsurance) || 0}
                    onChange={(e) => handleSliderChange('yearlyInsurance', e.target.value)}
                    className="form-slider"
                  />
                  <div className="slider-labels">
                    <span>$0</span>
                    <span>$10K</span>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="downpaymentPercentage">Downpayment Percentage (%)</label>
                  <input 
                    type="text" 
                    id="downpaymentPercentage"
                    name="downpaymentPercentage" 
                    value={formData.downpaymentPercentage} 
                    onChange={handleChange}
                    className="form-control" 
                  />
                  <input
                    type="range"
                    min={getSliderConfig('downpaymentPercentage').min}
                    max={getSliderConfig('downpaymentPercentage').max}
                    step={getSliderConfig('downpaymentPercentage').step}
                    value={parseFloat(formData.downpaymentPercentage) || 0}
                    onChange={(e) => handleSliderChange('downpaymentPercentage', e.target.value)}
                    className="form-slider"
                  />
                  <div className="slider-labels">
                    <span>0%</span>
                    <span>100%</span>
                  </div>
                </div>
              </div>
              
              <div className="form-column">
                <div className="form-group">
                  <label htmlFor="monthlyHOA">Monthly HOA</label>
                  <input 
                    type="text" 
                    id="monthlyHOA"
                    name="monthlyHOA" 
                    value={formData.monthlyHOA} 
                    onChange={handleChange}
                    className="form-control" 
                  />
                  <input
                    type="range"
                    min={getSliderConfig('monthlyHOA').min}
                    max={getSliderConfig('monthlyHOA').max}
                    step={getSliderConfig('monthlyHOA').step}
                    value={parseFloat(formData.monthlyHOA) || 0}
                    onChange={(e) => handleSliderChange('monthlyHOA', e.target.value)}
                    className="form-slider"
                  />
                  <div className="slider-labels">
                    <span>$0</span>
                    <span>$1K</span>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="managementFeePercentage">Management Fee (%)</label>
                  <input 
                    type="text" 
                    id="managementFeePercentage"
                    name="managementFeePercentage" 
                    value={formData.managementFeePercentage} 
                    onChange={handleChange}
                    className="form-control" 
                  />
                  <input
                    type="range"
                    min={getSliderConfig('managementFeePercentage').min}
                    max={getSliderConfig('managementFeePercentage').max}
                    step={getSliderConfig('managementFeePercentage').step}
                    value={parseFloat(formData.managementFeePercentage) || 0}
                    onChange={(e) => handleSliderChange('managementFeePercentage', e.target.value)}
                    className="form-slider"
                  />
                  <div className="slider-labels">
                    <span>0%</span>
                    <span>20%</span>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="vacancyRate">Vacancy Rate (%)</label>
                  <input 
                    type="text" 
                    id="vacancyRate"
                    name="vacancyRate" 
                    value={formData.vacancyRate} 
                    onChange={handleChange}
                    className="form-control" 
                  />
                  <input
                    type="range"
                    min={getSliderConfig('vacancyRate').min}
                    max={getSliderConfig('vacancyRate').max}
                    step={getSliderConfig('vacancyRate').step}
                    value={parseFloat(formData.vacancyRate) || 0}
                    onChange={(e) => handleSliderChange('vacancyRate', e.target.value)}
                    className="form-slider"
                  />
                  <div className="slider-labels">
                    <span>0%</span>
                    <span>20%</span>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="yearlyMaintenance">Annual Maintenance</label>
                  <input 
                    type="text" 
                    id="yearlyMaintenance"
                    name="yearlyMaintenance" 
                    value={formData.yearlyMaintenance} 
                    onChange={handleChange}
                    className="form-control" 
                  />
                  <input
                    type="range"
                    min={getSliderConfig('yearlyMaintenance').min}
                    max={getSliderConfig('yearlyMaintenance').max}
                    step={getSliderConfig('yearlyMaintenance').step}
                    value={parseFloat(formData.yearlyMaintenance) || 0}
                    onChange={(e) => handleSliderChange('yearlyMaintenance', e.target.value)}
                    className="form-slider"
                  />
                  <div className="slider-labels">
                    <span>$0</span>
                    <span>$20K</span>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="additionalExpenses">Additional Monthly Expense (e.g. monthly mortgage)</label>
                  <input 
                    type="text" 
                    id="additionalExpenses"
                    name="additionalExpenses" 
                    value={formData.additionalExpenses} 
                    onChange={handleChange}
                    className="form-control" 
                  />
                  <input
                    type="range"
                    min={getSliderConfig('additionalExpenses').min}
                    max={getSliderConfig('additionalExpenses').max}
                    step={getSliderConfig('additionalExpenses').step}
                    value={parseFloat(formData.additionalExpenses) || 0}
                    onChange={(e) => handleSliderChange('additionalExpenses', e.target.value)}
                    className="form-slider"
                  />
                  <div className="slider-labels">
                    <span>$0</span>
                    <span>$10K</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                onClick={handleRecalculate}
                className="btn-primary"
                disabled={loading}
              >
                {loading ? 'Processing...' : (isGuest && !user && recalculateCount >= 1 ? 'Sign Up to Recalculate' : 'Recalculate ROI')}
              </button>
            </div>
            
            {showAuthPrompt && (
              <div className="auth-prompt-modal">
                <div className="auth-prompt-content">
                  <h3>Sign Up to Continue</h3>
                  <p>To recalculate ROI and access full features, please create an account or log in.</p>
                  <div className="auth-prompt-actions">
                    <button onClick={() => navigate('/signup')} className="btn-primary">Sign Up</button>
                    <button onClick={() => navigate('/login')} className="btn-secondary">Log In</button>
                    <button onClick={() => setShowAuthPrompt(false)} className="btn-link">Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Property Comparison */}
        <PropertyComparison currentProperty={formData} />

      </div>
      
      {/* Limit Reached Modal */}
      <LimitReachedModal 
        show={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        limitType={limitType}
      />
    </div>
  );
};

export default FormPage;
